import {
	children,
	createEffect,
	createSignal,
	type JSX,
	on,
	onCleanup,
	type ParentProps,
	Show,
} from "solid-js";
import "./Carousel.css";
import chevronLeftIcon from "@ms/chevron_left.svg?raw";
import chevronRightIcon from "@ms/chevron_right.svg?raw";
import { Icon } from "@ui/Icon";

type CarouselProps = ParentProps<{
	class?: string;
	classList?: Record<string, boolean>;
	trackClass?: string;
	scrollMode?: "buttons" | "mousewheel";
	/** `snap`: one button press or wheel step centers the adjacent slide with smooth scrolling (respects `prefers-reduced-motion`). Default: free scroll. */
	scrollType?: "continuous" | "snap";
	layout?: "compact" | "standard";
	/**
	 * When true, the carousel grows to fill a flex parent (column) and slides stretch
	 * to the viewport height. Use with `layout="compact"` for full-height video carousels.
	 */
	fill?: boolean;
	/** Index of the slide to center horizontally in the viewport (default `0`). */
	startIndex?: number;
	/** Fired when the centered “active” real slide changes (after scroll/layout). */
	onActiveRealSlideChange?: (realSlideIndex: number | null) => void;
}>;

/** Sub-pixel tolerance for “at start / at end” only (not for detecting overflow). */
const SCROLL_EDGE_EPSILON = 2;

/**
 * Overflow below this is rounding noise from fractional slide widths rather than
 * anything worth offering scroll buttons for.
 */
const SCROLLABLE_OVERFLOW_MIN_PX = 2;

/** Applied to the track child whose center is closest to the viewport center. */
export const CAROUSEL_ACTIVE_SLIDE_CLASS = "gvtui-carousel__slide--active";

/**
 * Scrolls `element` (a slide, or anything inside one) fully into horizontal view
 * by the smallest amount that does so, honouring any inline padding on the track
 * as an inset. Only touches the carousel’s own scroll position, so it leaves the
 * page and any surrounding scrollers alone. A no-op when the element is already
 * fully visible or sits outside a carousel. Elements wider than the viewport are
 * aligned to its leading edge.
 */
export function scrollCarouselSlideIntoView(element: HTMLElement) {
	const viewport = element.closest<HTMLElement>(".gvtui-carousel__viewport");
	if (!viewport) {
		return;
	}

	const track = viewport.querySelector<HTMLElement>(".gvtui-carousel__track");
	const trackStyle = track ? getComputedStyle(track) : null;
	const insetStart = trackStyle
		? Number.parseFloat(trackStyle.paddingLeft) || 0
		: 0;
	const insetEnd = trackStyle
		? Number.parseFloat(trackStyle.paddingRight) || 0
		: 0;

	const elementRect = element.getBoundingClientRect();
	const viewportRect = viewport.getBoundingClientRect();
	const visibleStart = viewportRect.left + insetStart;
	const visibleEnd = viewportRect.right - insetEnd;

	let delta = 0;
	if (elementRect.left < visibleStart) {
		delta = elementRect.left - visibleStart;
	} else if (elementRect.right > visibleEnd) {
		// Never reveal the trailing edge by pushing the leading one out of view.
		delta = Math.min(
			elementRect.right - visibleEnd,
			elementRect.left - visibleStart,
		);
	}
	if (Math.abs(delta) < 1) {
		return;
	}

	const prefersReducedMotion = window.matchMedia(
		"(prefers-reduced-motion: reduce)",
	).matches;
	viewport.scrollTo({
		left: viewport.scrollLeft + delta,
		behavior: prefersReducedMotion ? "auto" : "smooth",
	});
}

function countRealSlides(track: Element): number {
	return track.children.length;
}

function realIndexFromDomIndex(_track: Element, domIdx: number): number {
	return domIdx;
}

/**
 * Left edge of `child` in the viewport’s scroll coordinate system (includes flex gap,
 * fractional widths from `cqi`, etc.) — matches what the browser actually laid out.
 */
function getChildLeftInScrollContent(
	viewportEl: HTMLElement,
	child: HTMLElement,
): number {
	return (
		viewportEl.scrollLeft +
		(child.getBoundingClientRect().left -
			viewportEl.getBoundingClientRect().left)
	);
}

/** DOM index of non-blank slide whose center is nearest the viewport center (snap “current”). */
function getSlideIndexClosestToViewportCenter(el: HTMLDivElement): number {
	const track = el.querySelector(".gvtui-carousel__track");
	if (!track) {
		return 0;
	}
	const n = track.children.length;
	if (n === 0) {
		return 0;
	}
	const vr = el.getBoundingClientRect();
	const viewportCenterX = (vr.left + vr.right) / 2;
	let bestIdx = -1;
	let bestDist = Infinity;
	for (let i = 0; i < n; i++) {
		const child = track.children[i] as HTMLElement;
		const cr = child.getBoundingClientRect();
		const cx = (cr.left + cr.right) / 2;
		const dist = Math.abs(cx - viewportCenterX);
		if (
			dist < bestDist ||
			(dist === bestDist && (bestIdx < 0 || i < bestIdx))
		) {
			bestDist = dist;
			bestIdx = i;
		}
	}
	return bestIdx < 0 ? 0 : bestIdx;
}

export const Carousel = (props: CarouselProps) => {
	const resolvedChildren = children(() => props.children);
	const realSlideCount = () => resolvedChildren.toArray().length;

	const [viewport, setViewport] = createSignal<HTMLDivElement | null>(null);
	const [isSyncingStartIndex, setIsSyncingStartIndex] = createSignal(false);

	const isCompactLayout = () => props.layout === "compact";
	const isFillLayout = () => props.fill === true;
	const [atStart, setAtStart] = createSignal(true);
	// Assume not at end until measured; avoids right arrow disabled before layout.
	const [atEnd, setAtEnd] = createSignal(false);
	// Assume nothing to scroll until measured, so the buttons appear once when
	// they are needed rather than flashing away on content that fits. Slides
	// holding images grow as those load; the track is observed for that.
	const [isScrollable, setIsScrollable] = createSignal(false);

	const showScrollButtons = () =>
		props.scrollMode === "buttons" && realSlideCount() > 1 && isScrollable();

	const updateScrollEdges = () => {
		const el = viewport();
		if (!el) {
			return;
		}

		const maxScroll = el.scrollWidth - el.clientWidth;
		const sl = el.scrollLeft;

		setIsScrollable(maxScroll > SCROLLABLE_OVERFLOW_MIN_PX);

		// Only treat as non-scrollable when there is no extra width at all (don’t use a
		// large epsilon here — that disabled the right arrow while content was still scrollable).
		if (maxScroll <= 0) {
			setAtStart(true);
			setAtEnd(true);
		} else {
			setAtStart(sl <= SCROLL_EDGE_EPSILON);
			// If maxScroll is tiny, don’t use (maxScroll - epsilon) or we’d mark “at end” at scrollLeft 0.
			const endThreshold =
				maxScroll <= SCROLL_EDGE_EPSILON
					? maxScroll
					: maxScroll - SCROLL_EDGE_EPSILON;
			setAtEnd(sl >= endThreshold);
		}

		updateActiveSlide(el);
	};

	/** Marks the slide whose center is nearest the viewport center. */
	const updateActiveSlide = (el: HTMLDivElement) => {
		const track = el.querySelector(".gvtui-carousel__track");
		if (!track) {
			return;
		}
		const { children } = track;
		const n = children.length;
		if (n === 0) {
			return;
		}
		const vr = el.getBoundingClientRect();
		const viewportCenterX = (vr.left + vr.right) / 2;
		type Candidate = { index: number; dist: number };
		const candidates: Candidate[] = [];
		for (let i = 0; i < n; i++) {
			const child = children[i] as HTMLElement;
			const cr = child.getBoundingClientRect();
			const cx = (cr.left + cr.right) / 2;
			candidates.push({
				index: i,
				dist: Math.abs(cx - viewportCenterX),
			});
		}
		let activeIndex: number | null = null;
		if (candidates.length > 0) {
			candidates.sort((a, b) => a.dist - b.dist || a.index - b.index);
			activeIndex = candidates[0].index;
		}
		if (isSyncingStartIndex() && props.startIndex !== undefined) {
			activeIndex = props.startIndex;
		}
		for (let i = 0; i < n; i++) {
			const ch = children[i] as HTMLElement;
			ch.classList.toggle(CAROUSEL_ACTIVE_SLIDE_CLASS, i === activeIndex);
		}
		const onActiveChange = props.onActiveRealSlideChange;
		if (onActiveChange) {
			const realIdx =
				activeIndex === null ? null : realIndexFromDomIndex(track, activeIndex);
			if (
				isSyncingStartIndex() &&
				props.startIndex !== undefined &&
				realIdx !== props.startIndex
			) {
				return;
			}
			onActiveChange(realIdx);
		}
	};

	/**
	 * Centers a slide in the viewport. When `alignFirstSlideToStart` is true (controlled
	 * `startIndex`), slide 0 is aligned to the start; otherwise slide 0 is centered like others (snap).
	 */
	const runAfterScrollComplete = (
		el: HTMLDivElement,
		targetScrollLeft: number,
		onComplete?: () => void,
	) => {
		if (!onComplete) {
			return;
		}
		let done = false;
		let stableFrames = 0;
		let frameId = 0;
		const start = performance.now();
		const settleEpsilon = 1;
		const stableFramesNeeded = 2;
		const maxWaitMs = 1500;
		const finish = () => {
			if (done) {
				return;
			}
			done = true;
			if (frameId) {
				cancelAnimationFrame(frameId);
			}
			onComplete();
		};
		const checkSettled = () => {
			if (done) {
				return;
			}
			if (Math.abs(el.scrollLeft - targetScrollLeft) <= settleEpsilon) {
				stableFrames++;
				if (stableFrames >= stableFramesNeeded) {
					finish();
					return;
				}
			} else {
				stableFrames = 0;
			}
			if (performance.now() - start >= maxWaitMs) {
				finish();
				return;
			}
			frameId = requestAnimationFrame(checkSettled);
		};
		frameId = requestAnimationFrame(checkSettled);
	};

	const scrollChildIndexIntoCenter = (
		el: HTMLDivElement,
		rawIdx: number,
		alignFirstSlideToStart: boolean,
		animateToCenter = false,
		onScrollComplete?: () => void,
	) => {
		const track = el.querySelector(".gvtui-carousel__track");
		if (!track) {
			return;
		}
		const { children } = track;
		const n = children.length;
		if (n === 0) {
			return;
		}
		const idx = Math.max(0, Math.min(Math.floor(rawIdx), n - 1));
		const childEl = children[idx] as HTMLElement;
		const realIdx = realIndexFromDomIndex(track, idx);
		const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
		const useFlushStart = alignFirstSlideToStart && realIdx === 0;
		if (useFlushStart) {
			el.scrollLeft = 0;
			updateScrollEdges();
			queueMicrotask(() => {
				onScrollComplete?.();
			});
			return;
		}
		const child = childEl;
		const childLeft = getChildLeftInScrollContent(el, child);
		const childWidth = child.offsetWidth;
		const viewportWidth = el.clientWidth;
		const targetScroll = Math.max(
			0,
			Math.min(childLeft - viewportWidth / 2 + childWidth / 2, maxScroll),
		);
		const prefersReducedMotion =
			typeof window !== "undefined" &&
			window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		const animateSnap =
			!alignFirstSlideToStart &&
			(animateToCenter || props.scrollType === "snap") &&
			!prefersReducedMotion;
		if (animateSnap) {
			el.scrollTo({ left: targetScroll, behavior: "smooth" });
			queueMicrotask(updateScrollEdges);
			runAfterScrollComplete(el, targetScroll, onScrollComplete);
		} else {
			el.scrollLeft = targetScroll;
			updateScrollEdges();
			queueMicrotask(() => {
				onScrollComplete?.();
			});
		}
	};

	const scrollStartIndexIntoCenter = () => {
		const el = viewport();
		if (!el) {
			setIsSyncingStartIndex(false);
			return;
		}
		const track = el.querySelector(".gvtui-carousel__track");
		if (!track) {
			setIsSyncingStartIndex(false);
			return;
		}
		const realChildren = countRealSlides(track);
		if (realChildren === 0) {
			setIsSyncingStartIndex(false);
			return;
		}
		const raw = props.startIndex;
		const realIdx = Math.max(
			0,
			Math.min(Math.floor(raw === undefined ? 0 : raw), realChildren - 1),
		);
		setIsSyncingStartIndex(true);
		scrollChildIndexIntoCenter(el, realIdx, true, false, () => {
			setIsSyncingStartIndex(false);
			updateScrollEdges();
		});
	};

	const syncStartIndexIntoCenter = (attempt = 0) => {
		const el = viewport();
		if (!el) {
			setIsSyncingStartIndex(false);
			return;
		}
		if (attempt > 40) {
			setIsSyncingStartIndex(false);
			return;
		}
		const track = el.querySelector(".gvtui-carousel__track");
		if (!track || track.children.length === 0) {
			requestAnimationFrame(() => {
				syncStartIndexIntoCenter(attempt + 1);
			});
			return;
		}
		queueMicrotask(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					scrollStartIndexIntoCenter();
				});
			});
		});
	};

	const snapStep = (direction: -1 | 1) => {
		const el = viewport();
		if (!el) {
			return;
		}
		const track = el.querySelector(".gvtui-carousel__track");
		if (!track) {
			return;
		}
		const realCount = countRealSlides(track);
		if (realCount === 0) {
			return;
		}
		const currentDom = getSlideIndexClosestToViewportCenter(el);
		const currentReal = realIndexFromDomIndex(track, currentDom);
		const nextReal = Math.max(
			0,
			Math.min(realCount - 1, currentReal + direction),
		);
		scrollChildIndexIntoCenter(el, nextReal, false);
	};

	createEffect(() => {
		const el = viewport();
		if (!el) {
			return;
		}
		// Before any queued `onScroll` → `updateActiveSlide`, so the first paint does not
		// mark slide 0 active while `startIndex` targets another slide (see second effect).
		if (props.startIndex !== undefined) {
			setIsSyncingStartIndex(true);
		}

		const onScroll = () => {
			updateScrollEdges();
		};

		el.addEventListener("scroll", onScroll, { passive: true });

		const ro = new ResizeObserver(() => {
			if (props.startIndex !== undefined) {
				setIsSyncingStartIndex(true);
				syncStartIndexIntoCenter();
				return;
			}
			onScroll();
		});
		ro.observe(el);
		const track = el.querySelector(".gvtui-carousel__track");
		if (track) {
			ro.observe(track);
		}

		queueMicrotask(onScroll);
		requestAnimationFrame(onScroll);
		requestAnimationFrame(() => {
			requestAnimationFrame(onScroll);
		});

		onCleanup(() => {
			el.removeEventListener("scroll", onScroll);
			ro.disconnect();
		});
	});

	createEffect(
		on([viewport, () => props.layout], () => {
			const el = viewport();
			if (!el) {
				return;
			}
			queueMicrotask(() => {
				if (props.startIndex !== undefined) {
					setIsSyncingStartIndex(true);
					syncStartIndexIntoCenter();
					return;
				}
				updateScrollEdges();
			});
		}),
	);

	/**
	 * Scroll to `startIndex` whenever it changes, when the viewport mounts,
	 * or when slides appear (retry). Uses `on()` so prop updates reliably re-run the effect.
	 */
	createEffect(
		on([() => props.startIndex, () => props.layout, viewport], () => {
			const el = viewport();
			if (!el) {
				return;
			}
			let cancelled = false;
			onCleanup(() => {
				cancelled = true;
			});

			const tryScroll = (attempt: number) => {
				if (cancelled) {
					return;
				}
				if (attempt > 40) {
					return;
				}
				const track = el.querySelector(".gvtui-carousel__track");
				if (!track || track.children.length === 0) {
					requestAnimationFrame(() => tryScroll(attempt + 1));
					return;
				}
				queueMicrotask(() => {
					if (cancelled) {
						return;
					}
					requestAnimationFrame(() => {
						if (cancelled) {
							return;
						}
						requestAnimationFrame(() => {
							if (cancelled) {
								return;
							}
							scrollStartIndexIntoCenter();
						});
					});
				});
			};
			tryScroll(0);
		}),
	);

	const onCarouselWheel = (event: WheelEvent) => {
		if (props.scrollMode === "buttons") {
			return;
		}
		const el = viewport();
		if (!el) {
			return;
		}

		const delta =
			Math.abs(event.deltaY) > Math.abs(event.deltaX)
				? event.deltaY
				: event.deltaX;

		if (delta === 0) {
			return;
		}

		event.preventDefault();
		if (props.scrollType === "snap") {
			snapStep(delta > 0 ? 1 : -1);
			return;
		}
		el.scrollLeft += delta;
		queueMicrotask(updateScrollEdges);
	};

	let scrollIntervalId: ReturnType<typeof setInterval> | undefined;

	const startScrollLeft = () => {
		const el = viewport();
		if (!el) {
			return;
		}
		scrollIntervalId = setInterval(() => {
			el.scrollLeft -= 5;
		}, 10);
	};

	const startScrollRight = () => {
		const el = viewport();
		if (!el) {
			return;
		}
		scrollIntervalId = setInterval(() => {
			el.scrollLeft += 5;
		}, 10);
	};

	const stopArrowScroll = () => {
		if (scrollIntervalId !== undefined) {
			clearInterval(scrollIntervalId);
			scrollIntervalId = undefined;
		}
		queueMicrotask(updateScrollEdges);
	};

	const containerClass = () =>
		props.class ? `gvtui-carousel ${props.class}` : "gvtui-carousel";

	const trackClass = () => {
		const base = "gvtui-carousel__track";
		const extra = props.trackClass ? ` ${props.trackClass}` : "";
		const compact = isCompactLayout()
			? " gvtui-carousel__track--compact gvtui-carousel__track--visible-items"
			: "";
		return `${base}${extra}${compact}`;
	};

	const viewportStyle = (): JSX.CSSProperties | undefined => {
		if (!isCompactLayout()) {
			return undefined;
		}
		return {
			"--carousel-visible-items": "1",
			"--carousel-gap": "0rem",
		};
	};

	const scrollButtonProps = (direction: -1 | 1) => {
		const isSnap = props.scrollType === "snap";
		const isLeft = direction === -1;
		return {
			disabled: isLeft ? atStart() : atEnd(),
			type: "button" as const,
			class: "scroll-button",
			classList: {
				"fixed-left": isLeft,
				"fixed-right": !isLeft,
			},
			onClick: isSnap
				? () => {
						snapStep(direction);
					}
				: undefined,
			onMouseDown: isSnap
				? undefined
				: isLeft
					? startScrollLeft
					: startScrollRight,
			onMouseUp: isSnap ? undefined : stopArrowScroll,
			onMouseLeave: isSnap ? undefined : stopArrowScroll,
		};
	};

	return (
		<div
			class="gvtui-carousel-container"
			classList={{
				[`gvtui-carousel-container--${props.layout ?? "standard"}`]: true,
				"gvtui-carousel-container--fill": isFillLayout(),
			}}
		>
			<div
				class={containerClass()}
				classList={{
					"gvtui-carousel--at-start": atStart(),
					"gvtui-carousel--at-end": atEnd(),
					"gvtui-carousel--compact": isCompactLayout(),
					"gvtui-carousel--fill": isFillLayout(),
					"gvtui-carousel--scroll-buttons": showScrollButtons(),
					...(props.classList ?? {}),
				}}
			>
				<Show when={showScrollButtons()}>
					<button {...scrollButtonProps(-1)}>
						<Icon icon={chevronLeftIcon} />
					</button>
				</Show>
				<div
					ref={setViewport}
					class="gvtui-carousel__viewport"
					classList={{
						"gvtui-carousel__viewport--visible-items": isCompactLayout(),
					}}
					style={viewportStyle()}
					onWheel={onCarouselWheel}
				>
					<div class={trackClass()}>{props.children}</div>
				</div>
				<Show when={showScrollButtons()}>
					<button {...scrollButtonProps(1)}>
						<Icon icon={chevronRightIcon} />
					</button>
				</Show>
			</div>
		</div>
	);
};
